<template>
    <card title="Compound Batch Verification">
        <div>
            <b-row class="mb-3">
                <b-col cols="3">
                    <label>Store</label>
                    <b-form-select v-model="storeFilter" :options="storeOptions" />
                </b-col>
                <b-col cols="3">
                    <fg-input v-model="compoundNameFilter" label="Compound Name" placeholder="Filter by name" />
                </b-col>
                <b-col cols="2">
                    <label>Start Date</label>
                    <fg-input v-model="startDate" type="date" />
                </b-col>
                <b-col cols="2">
                    <label>End Date</label>
                    <fg-input v-model="endDate" type="date" />
                </b-col>
                <b-col cols="2" class="pt-4">
                    <b-button variant="outline-secondary" size="sm" @click="resetFilters">
                        <i class="fas fa-undo" /> Reset
                    </b-button>
                </b-col>
            </b-row>

            <div v-if="loading" class="text-center py-4">
                <b-spinner /> Loading batches awaiting verification...
            </div>

            <b-table
                v-else-if="filteredBatches.length > 0"
                :items="filteredBatches"
                :fields="tableFields"
                hover
                striped
                responsive
                class="cursor-pointer"
                @row-clicked="navigateToVerification">
                <template #cell(batchNumber)="data">
                    <strong>{{ data.value }}</strong>
                </template>
                <template #cell(compoundName)="data">
                    {{ data.item.compound?.name || 'Unknown' }}
                </template>
                <template #cell(batchSize)="data">
                    {{ data.value }} {{ data.item.compound?.unitOfMeasure || '' }}
                </template>
                <template #cell(storeName)="data">
                    {{ data.value || 'N/A' }}
                </template>
                <template #cell(created)="data">
                    {{ formatDate(data.value) }}
                </template>
                <template #cell(verificationProgress)="data">
                    <span v-if="data.item.requiredVerificationCount > 0">
                        <b-badge :variant="data.item.completedVerificationCount === data.item.requiredVerificationCount ? 'success' : 'warning'">
                            {{ data.item.completedVerificationCount || 0 }} / {{ data.item.requiredVerificationCount }}
                        </b-badge>
                    </span>
                    <span v-else class="text-muted">No steps require sign-off</span>
                </template>
                <template #cell(reviewFlags)="data">
                    <span v-if="reviewFlagSummary(data.item).length > 0">
                        <b-badge v-for="flag in reviewFlagSummary(data.item)" :key="flag.code"
                            :variant="data.item.reviewAcknowledgedBy ? 'secondary' : 'warning'" class="mr-1"
                            :title="describeSummaryEntry(flag)">
                            {{ flag.label }}<span v-if="flag.count > 1"> ×{{ flag.count }}</span>
                        </b-badge>
                        <b-badge v-if="data.item.reviewAcknowledgedBy" variant="success"
                            :title="`Acknowledged by ${data.item.reviewAcknowledgedBy}`">
                            <i class="fas fa-check" /> Acknowledged
                        </b-badge>
                        <small v-else class="text-warning d-block">Needs acknowledgement</small>
                    </span>
                    <span v-else class="text-muted">None</span>
                </template>
            </b-table>

            <b-alert v-else-if="!loading" variant="info" show>
                <i class="fas fa-check-circle" /> No compound batches are currently awaiting verification.
            </b-alert>
        </div>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { CompoundBatch } from '@/models/Drug/CompoundDrug';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { ReviewFlagSummary, summarizeReviewFlags } from '@/util/compoundBatchReview';

@Component({
    name: "CompoundBatchVerificationQueuePage"
})
export default class CompoundBatchVerificationQueuePage extends Vue {
    private batches: CompoundBatch[] = [];
    private loading = false;
    private storeFilter = '';
    private compoundNameFilter = '';
    private startDate = '';
    private endDate = '';

    private tableFields = [
        { key: 'batchNumber', label: 'Batch Number', sortable: true },
        { key: 'compoundName', label: 'Compound Name', sortable: true },
        { key: 'batchSize', label: 'Batch Size', sortable: true },
        { key: 'storeName', label: 'Store', sortable: true },
        { key: 'created', label: 'Created Date', sortable: true },
        { key: 'createdBy', label: 'Created By', sortable: true },
        { key: 'verificationProgress', label: 'Step Verification', sortable: false },
        { key: 'reviewFlags', label: 'Review Flags', sortable: false }
    ];

    get storeOptions() {
        const stores = new Set<string>();
        this.batches.forEach(b => {
            if (b.storeName) stores.add(b.storeName);
        });
        const options: { value: string; text: string }[] = [{ value: '', text: 'All Stores' }];
        stores.forEach(s => options.push({ value: s, text: s }));
        return options;
    }

    get filteredBatches(): CompoundBatch[] {
        let filtered = [...this.batches];

        if (this.storeFilter) {
            filtered = filtered.filter(b => b.storeName === this.storeFilter);
        }

        if (this.compoundNameFilter) {
            const search = this.compoundNameFilter.toLowerCase();
            filtered = filtered.filter(b => b.compound?.name?.toLowerCase().includes(search));
        }

        if (this.startDate) {
            const start = new Date(this.startDate);
            filtered = filtered.filter(b => new Date(b.created) >= start);
        }

        if (this.endDate) {
            const end = new Date(this.endDate);
            end.setDate(end.getDate() + 1);
            filtered = filtered.filter(b => new Date(b.created) <= end);
        }

        return filtered;
    }

    async mounted() {
        await this.loadBatches();
    }

    async loadBatches() {
        this.loading = true;
        try {
            const response = await Axios.get<CompoundBatch[]>('/CompoundBatch/awaiting-verification');
            this.batches = response.data;
        } catch (error) {
            console.error('Error loading batches awaiting verification:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    resetFilters() {
        this.storeFilter = '';
        this.compoundNameFilter = '';
        this.startDate = '';
        this.endDate = '';
    }

    /** What holds each batch — one badge per kind of review flag, with how many rows carry it. */
    reviewFlagSummary(batch: CompoundBatch): ReviewFlagSummary[] {
        return summarizeReviewFlags(batch.reviewFlags);
    }

    describeSummaryEntry(entry: ReviewFlagSummary): string {
        switch (entry.code) {
            case 'MissingCoa':
                return `${entry.count} ingredient${entry.count === 1 ? '' : 's'} filled from a lot with no usable COA (factor 1.0 applied)`;
            case 'ManualOverride':
                return `${entry.count} weigh-out${entry.count === 1 ? '' : 's'} overridden by hand`;
            default:
                return `${entry.count} review flag${entry.count === 1 ? '' : 's'} of kind ${entry.code}`;
        }
    }

    navigateToVerification(batch: CompoundBatch) {
        this.$router.push({
            name: 'CompoundBatchVerificationDetails',
            params: { batchId: batch.id.toString() }
        });
    }

    formatDate(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    }
}
</script>

<style scoped>
.cursor-pointer >>> tbody tr {
    cursor: pointer;
}
</style>
